/* eslint-disable @typescript-eslint/no-explicit-any */
import { logMosaic } from './log';
import { PermutiveClient, PermutiveEventProperties } from './permutiveEvent';

/**
 * Permutive-shaped ad-slot events (Mosaic Phase 6, DAP-114765):
 *   - `SlotViewable` : GPT `impressionViewable` (Active View: >=50% of the ad's
 *                      pixels in the viewport for >=1 continuous second)
 *   - `SlotClicked`  : GPT has no native click event; detected via the
 *                      window-blur / active ad-iframe heuristic.
 *
 * Ad attribution (advertiser_id, campaign_id, is_empty, size) arrives on the GPT
 * `slotRenderEnded` event, so we cache it per slot on render and merge it into
 * the viewable/click events.
 *
 * GPT does not replay past events to listeners added later, and editorial pages
 * usually render slots before `load`. So on install we also BACKFILL the cache
 * from any slots GPT has already rendered, via
 * `slot.getResponseInformation()`. Without that, late-registered listeners emit
 * SlotViewable with empty advertiser/campaign ids (or miss it entirely).
 */

export const SLOT_VIEWABLE_EVENT = 'SlotViewable';
export const SLOT_CLICKED_EVENT = 'SlotClicked';

/** GPT names its creative iframes `google_ads_iframe_*`. Used to tell a real ad
 * iframe apart from unrelated page iframes (video players, social embeds). */
const GPT_IFRAME_PREFIX = 'google_ads_iframe';

/**
 * How often to reconcile `document.activeElement` for ad clicks the focus and
 * blur events do not report. Cheap: the check exits on a property read unless
 * the focused element actually changed.
 */
const FOCUS_POLL_MS = 250;

export type SlotSize = { width: number; height: number };

export type SlotProperties = PermutiveEventProperties & {
  client?: PermutiveClient;
  /** `null` when GPT gave us no attribution (unknown), never a fabricated ''. */
  advertiser_id: string | null;
  campaign_id: string | null;
  ad_unit_path: string;
  slot_element_id: string;
  size: SlotSize | null;
  /** `null` when we genuinely cannot tell whether the slot was filled. */
  is_empty: boolean | null;
  service_name: string;
};

/** Raw slot attribution gathered from GPT events, before Permutive mapping. */
export type SlotAttribution = {
  advertiserId?: number | string | null;
  campaignId?: number | string | null;
  adUnitPath?: string | null;
  slotElementId?: string | null;
  size?: [number, number] | { width: number; height: number } | string | null;
  isEmpty?: boolean | null;
  serviceName?: string | null;
};

/**
 * Normalize a GPT size (array `[w, h]`, object, or `'fluid'`) to
 * `{width,height}`. A non-positive size is treated as unknown rather than
 * reported literally: GPT sends `0x0` for a slot GAM did not fill, and `0x0` is
 * not a real creative dimension.
 */
export function normalizeSize(size: SlotAttribution['size']): SlotSize | null {
  let candidate: SlotSize | null = null;
  if (Array.isArray(size) && size.length >= 2 && typeof size[0] === 'number' && typeof size[1] === 'number') {
    candidate = { width: size[0], height: size[1] };
  } else if (size && typeof size === 'object' && typeof (size as any).width === 'number') {
    candidate = { width: (size as any).width, height: (size as any).height };
  }
  if (!candidate || !(candidate.width > 0) || !(candidate.height > 0)) {
    return null;
  }
  return candidate;
}

/** Map raw GPT slot attribution to Permutive-shaped `SlotProperties`. */
export function buildSlotProperties(src: SlotAttribution & { client?: PermutiveClient }): SlotProperties {
  const properties: SlotProperties = {
    advertiser_id: src.advertiserId != null ? String(src.advertiserId) : null,
    campaign_id: src.campaignId != null ? String(src.campaignId) : null,
    ad_unit_path: src.adUnitPath || '',
    slot_element_id: src.slotElementId || '',
    size: normalizeSize(src.size),
    is_empty: src.isEmpty == null ? null : Boolean(src.isEmpty),
    service_name: src.serviceName || '',
  };
  if (src.client) {
    properties.client = src.client;
  }
  return properties;
}

function getAdUnitPath(slot: any): string | null {
  try {
    return slot && typeof slot.getAdUnitPath === 'function' ? slot.getAdUnitPath() : null;
  } catch {
    return null;
  }
}

function getSlotElementId(slot: any): string | null {
  try {
    return slot && typeof slot.getSlotElementId === 'function' ? slot.getSlotElementId() : null;
  } catch {
    return null;
  }
}

/** GPT's post-render attribution for a slot; `null` when the slot is unfilled. */
function getResponseInformation(slot: any): any | null {
  try {
    return slot && typeof slot.getResponseInformation === 'function' ? slot.getResponseInformation() : null;
  } catch {
    return null;
  }
}

/** Measured size of a creative iframe, used when GPT gave us no usable size. */
function iframeSize(iframe: any): SlotSize | null {
  if (!iframe) {
    return null;
  }
  try {
    const width = Number(iframe.width || iframe.offsetWidth);
    const height = Number(iframe.height || iframe.offsetHeight);
    if (isFinite(width) && isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Rendered size of a slot, read from its GPT iframe. `slotRenderEnded` carries
 * the size directly; this is only used when backfilling a slot that rendered
 * before we subscribed.
 */
function renderedSize(slotElementId: string | null, doc: any): SlotSize | null {
  if (!slotElementId || !doc || typeof doc.getElementById !== 'function') {
    return null;
  }
  try {
    const container = doc.getElementById(slotElementId);
    return iframeSize(
      container && typeof container.querySelector === 'function' ? container.querySelector('iframe') : null,
    );
  } catch {
    return null;
  }
}

/**
 * GPT names creative iframes `google_ads_iframe_<adUnitPath>_<n>`, so the ad
 * unit path can be recovered from the id when the GPT API itself is unreachable
 * (blocked script, cross-frame console, consent-gated load).
 */
export function adUnitPathFromIframeId(id: string | null | undefined): string | null {
  const raw = String(id || '');
  if (raw.indexOf(`${GPT_IFRAME_PREFIX}_`) !== 0) {
    return null;
  }
  // Strip the prefix, GPT's trailing `_<n>` index, and its `__hidden__` suffix.
  const path = raw
    .slice(GPT_IFRAME_PREFIX.length + 1)
    .replace(/__hidden__$/, '')
    .replace(/_\d+$/, '');
  return path.indexOf('/') === 0 ? path : null;
}

/** Extract attribution from a GPT `slotRenderEnded` event object. */
export function attributionFromRenderEnded(event: any): SlotAttribution {
  const slot = event?.slot;
  return {
    advertiserId: event?.advertiserId ?? null,
    campaignId: event?.campaignId ?? null,
    adUnitPath: getAdUnitPath(slot),
    slotElementId: getSlotElementId(slot),
    size: event?.size ?? null,
    isEmpty: event?.isEmpty ?? null,
    serviceName: event?.serviceName ?? null,
  };
}

/**
 * Rebuild attribution directly from a live GPT slot object. Used to backfill
 * slots that rendered before our listeners were registered, and as the fallback
 * when an `impressionViewable` arrives for an uncached slot.
 */
export function attributionFromSlot(slot: any, doc?: any): SlotAttribution {
  const response = getResponseInformation(slot);
  const slotElementId = getSlotElementId(slot);
  return {
    advertiserId: response?.advertiserId ?? null,
    campaignId: response?.campaignId ?? null,
    adUnitPath: getAdUnitPath(slot),
    slotElementId,
    size: renderedSize(slotElementId, doc),
    // No response information => GPT has not filled this slot. `null` (unknown)
    // rather than `false`, which would assert the slot *was* filled.
    isEmpty: response ? false : null,
    serviceName: null,
  };
}

/** Extract the minimal attribution available from an event that only carries a slot. */
export function attributionFromSlotEvent(event: any): SlotAttribution {
  const slot = event?.slot;
  return {
    adUnitPath: getAdUnitPath(slot),
    slotElementId: getSlotElementId(slot),
    serviceName: event?.serviceName ?? null,
  };
}

export type GptSlotHandlers = {
  onSlotViewable: (attribution: SlotAttribution) => void;
  onSlotClicked: (attribution: SlotAttribution) => void;
};

/** Seed the cache from slots GPT has already rendered (see module docs). */
export function seedSlotCache(pubads: any, cache: Map<string, SlotAttribution>, doc?: any): void {
  try {
    const slots = typeof pubads?.getSlots === 'function' ? pubads.getSlots() : null;
    (slots || []).forEach((slot: any) => {
      const attribution = attributionFromSlot(slot, doc);
      if (attribution.slotElementId && !cache.has(attribution.slotElementId)) {
        cache.set(attribution.slotElementId, attribution);
      }
    });
  } catch {
    // ignore
  }
}

/** True when the element is one of GPT's creative iframes. */
function isGptAdIframe(iframe: any): boolean {
  const id = String(iframe?.id || '');
  const name = String(iframe?.name || '');
  return id.indexOf(GPT_IFRAME_PREFIX) === 0 || name.indexOf(GPT_IFRAME_PREFIX) === 0;
}

/**
 * Nearest ancestor whose id is a slot element id GPT actually knows about.
 * `knownIds` must include live GPT slot ids, not just cached ones.
 */
function findKnownSlotAncestor(iframe: any, knownIds: Set<string>): string | null {
  let el: any = iframe?.parentElement || null;
  while (el) {
    if (el.id && knownIds.has(el.id)) {
      return el.id;
    }
    el = el.parentElement;
  }
  return null;
}

/** Nearest ancestor element carrying an id, whatever it is. */
function nearestContainerId(iframe: any): string | null {
  const nearest = iframe?.closest ? iframe.closest('div[id]') : null;
  if (nearest?.id) {
    return nearest.id;
  }
  let el: any = iframe?.parentElement || null;
  while (el) {
    if (el.id) {
      return el.id;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Resolve the slot element id that owns a creative iframe, preferring an
 * ancestor GPT knows about. The nearest-container fallback can pick an
 * unrelated layout wrapper, so it is only a last resort.
 */
function findSlotKeyForIframe(iframe: any, knownIds: Set<string>): string | null {
  return findKnownSlotAncestor(iframe, knownIds) || nearestContainerId(iframe);
}

/**
 * Container ids that read as an ad slot, matching `ad`/`ads` as a whole word so
 * incidental substrings ("download", "related") do not qualify. Deliberately
 * loose on the surrounding convention, since brands name slots very differently
 * (CNN uses `ad_multi_atf_01`, not GPT's `div-gpt-ad-*`).
 */
const AD_CONTAINER_ID_PATTERN = /(^|[^a-z])ads?([^a-z]|$)/i;

function looksLikeAdContainer(id: string | null): boolean {
  return !!id && AD_CONTAINER_ID_PATTERN.test(id);
}

/**
 * Does a focused iframe represent a click on an ad?
 *
 * Requiring the focused element to be a `google_ads_iframe_*` element is too
 * strict: ad stacks and SafeFrame wrap the creative in their own iframe, and
 * that outer iframe (the one that actually receives focus) carries no GPT
 * marker. Accepting any iframe inside a container GPT owns covers those cases
 * while still excluding unrelated page iframes — video players and
 * social/comment embeds do not live inside a GPT slot container, which is the
 * false positive the prefix check was added for.
 */
function isAdClickTarget(iframe: any, knownIds: Set<string>): boolean {
  if (isGptAdIframe(iframe) || findKnownSlotAncestor(iframe, knownIds)) {
    return true;
  }
  // Fully anonymous iframe (no id, no name): the ad stack rendered the creative
  // in its own frame, so GPT's marker is a level down inside a cross-origin
  // document and unreachable from here. The container id is the only signal
  // left. Requiring anonymity keeps identifiable page iframes (video players,
  // social/comment embeds) out.
  const anonymous = !iframe?.id && !iframe?.name;
  return anonymous && looksLikeAdContainer(nearestContainerId(iframe));
}

/**
 * Reconcile GPT's view of a slot against the creative actually on the page.
 *
 * Two cases need this. GPT reports `'fluid'` for native creatives, which has no
 * dimensions. And ad-recovery stacks (Blockthrough and similar) fill slots
 * outside GPT entirely, so GPT reports `isEmpty: true, size: 0x0` for a slot
 * that visibly rendered an ad. Measuring the creative recovers the size in both
 * cases, and a measured creative disproves `isEmpty` — reporting a slot as empty
 * on a click the user just made would be self-contradictory.
 */
function withCreativeEvidence(attribution: SlotAttribution, iframe: any): SlotAttribution {
  const size = normalizeSize(attribution.size) || iframeSize(iframe);
  const reconciled: SlotAttribution = { ...attribution, size };
  if (attribution.isEmpty === true && size) {
    reconciled.isEmpty = false;
    logMosaic('GPT reported the slot empty but a creative was measured; treating as filled', {
      slotElementId: attribution.slotElementId,
      size,
    });
  }
  return reconciled;
}

function findSlotByElementId(pubads: any, slotElementId: string): any | null {
  try {
    const slots = typeof pubads?.getSlots === 'function' ? pubads.getSlots() : null;
    return (slots || []).find((slot: any) => getSlotElementId(slot) === slotElementId) || null;
  } catch {
    return null;
  }
}

/**
 * Wire GPT (`googletag`) events to Mosaic slot handlers. Caches per-slot
 * attribution from `slotRenderEnded`, backfills slots that already rendered,
 * emits SlotViewable on `impressionViewable`, and emits SlotClicked by detecting
 * focus moving into an ad iframe (GPT creatives render in iframes, so a click
 * that focuses one is a strong click signal), deduped per focus episode. Focus
 * is detected by reconciling `document.activeElement`, since browsers dispatch
 * no focus event for a click into a cross-origin iframe and window `blur` only
 * fires when the top window had focus to lose.
 *
 * Call this as early as possible — GPT will not replay events that fired before
 * the listeners were added. Safe to call before GPT loads: it queues into
 * `googletag.cmd`. Returns a teardown function that removes the listeners.
 */
export function installGptSlotTracking(handlers: GptSlotHandlers, win: any = window): () => void {
  const cache = new Map<string, SlotAttribution>();
  // Set once GPT runs our command queue entry; lets the click path query live
  // slots even when nothing was ever cached.
  let pubadsRef: any = null;

  const googletag = (win.googletag = win.googletag || { cmd: [] });
  googletag.cmd = googletag.cmd || [];

  googletag.cmd.push(() => {
    try {
      const pubads = typeof googletag.pubads === 'function' ? googletag.pubads() : null;
      if (!pubads || typeof pubads.addEventListener !== 'function') {
        return;
      }
      pubadsRef = pubads;

      // Recover attribution for slots that rendered before we subscribed.
      seedSlotCache(pubads, cache, win.document);

      pubads.addEventListener('slotRenderEnded', (event: any) => {
        const attribution = attributionFromRenderEnded(event);
        if (attribution.slotElementId) {
          cache.set(attribution.slotElementId, attribution);
        }
      });

      pubads.addEventListener('impressionViewable', (event: any) => {
        const key = getSlotElementId(event?.slot);
        const cached = key ? cache.get(key) : undefined;
        const attribution = cached || attributionFromSlot(event?.slot, win.document);
        handlers.onSlotViewable({
          ...attribution,
          serviceName: event?.serviceName ?? attribution.serviceName ?? null,
        });
      });
    } catch {
      // Defensive: never let GPT wiring break page load.
    }
  });

  /** Slot element ids GPT knows about: cached plus currently live. */
  const knownSlotIds = (): Set<string> => {
    const ids = new Set<string>(cache.keys());
    try {
      const slots = typeof pubadsRef?.getSlots === 'function' ? pubadsRef.getSlots() : null;
      (slots || []).forEach((slot: any) => {
        const id = getSlotElementId(slot);
        if (id) {
          ids.add(id);
        }
      });
    } catch {
      // ignore
    }
    return ids;
  };

  /**
   * Attribution for a clicked creative. The cache is the best source, but a
   * click can land on a slot we never saw render, so fall back to the live GPT
   * slot and finally to the iframe element itself, which still yields a real
   * size and ad unit path.
   */
  const clickAttribution = (iframe: any, key: string | null): SlotAttribution => {
    const cached = key ? cache.get(key) : undefined;
    if (cached) {
      return withCreativeEvidence(cached, iframe);
    }
    const slot = key ? findSlotByElementId(pubadsRef, key) : null;
    if (slot) {
      return withCreativeEvidence(attributionFromSlot(slot, win.document), iframe);
    }
    return {
      adUnitPath: adUnitPathFromIframeId(iframe?.id || iframe?.name),
      slotElementId: key,
      size: iframeSize(iframe),
      isEmpty: null,
    };
  };

  // One "focus episode" is a continuous period where a single ad iframe holds
  // focus. At most one SlotClicked per episode, so the `focusin` signal and the
  // `blur` backstop below can both run without double counting.
  let episode: { iframe: any; emitted: boolean } | null = null;

  const considerAdFocus = (candidate: any, source: string): boolean => {
    if (!candidate || candidate.tagName !== 'IFRAME') {
      return false;
    }
    if (episode && episode.iframe === candidate && episode.emitted) {
      return false;
    }
    const knownIds = knownSlotIds();
    if (!isAdClickTarget(candidate, knownIds)) {
      logMosaic(`ignored ${source} (focused iframe is not an ad)`, {
        id: candidate.id,
        name: candidate.name,
        nearestDivId: candidate.closest ? candidate.closest('div[id]')?.id : undefined,
        knownSlotIds: Array.from(knownIds),
      });
      return false;
    }
    episode = { iframe: candidate, emitted: true };
    logMosaic(`SlotClicked detected via ${source}`);
    handlers.onSlotClicked(clickAttribution(candidate, findSlotKeyForIframe(candidate, knownIds)));
    return true;
  };

  // Fast path when the top window did hold focus. `activeElement` can still be
  // <body> at blur time, hence the deferred re-check.
  //
  // There is deliberately no `focusin` listener: browsers do not dispatch focus
  // events for a click landing inside a cross-origin ad iframe, so it never
  // fires here and would only look like a working signal.
  const onBlur = () => {
    try {
      if (considerAdFocus(win.document && win.document.activeElement, 'blur')) {
        return;
      }
      if (typeof win.setTimeout === 'function') {
        win.setTimeout(() => {
          try {
            considerAdFocus(win.document && win.document.activeElement, 'deferred blur');
          } catch {
            // ignore
          }
        }, 0);
      }
    } catch {
      // ignore
    }
  };

  win.addEventListener('blur', onBlur);
  const doc = win.document;

  /**
   * Primary signal, because window blur only fires when the top window has
   * focus to lose.
   *
   * Once focus sits inside one cross-origin ad iframe, clicking a *different* ad
   * moves focus between two nested browsing contexts without the top window ever
   * regaining focus — so no `blur` fires, and only the first ad click of a visit
   * was ever recorded. `activeElement` still reflects the change, so reconciling
   * it on an interval catches the transition whatever the browser dispatches.
   *
   * Only transitions count, so an ad already holding focus when we install is
   * never mistaken for a click.
   */
  let lastActive: any = doc ? doc.activeElement : null;
  const pollFocus = () => {
    try {
      const active: any = doc && doc.activeElement;
      if (active === lastActive) {
        return;
      }
      lastActive = active;
      // Focus left the ad we already counted, so a later click on it counts again.
      if (episode && active !== episode.iframe) {
        episode = null;
      }
      considerAdFocus(active, 'focus poll');
    } catch {
      // ignore
    }
  };
  const pollHandle = typeof win.setInterval === 'function' ? win.setInterval(pollFocus, FOCUS_POLL_MS) : null;

  return () => {
    win.removeEventListener('blur', onBlur);
    if (pollHandle != null && typeof win.clearInterval === 'function') {
      win.clearInterval(pollHandle);
    }
  };
}
